import { Box, Tooltip } from '@chakra-ui/react';

import type { Quake } from '../api/types';

interface ScoreGaugeProps {
  score: number;
  mover?: Quake;
  small?: boolean;
}

// ScoreGauge renders heat with a tooltip-style evidence breakdown:
// Volume, Capital, Move, Spread quality, Depth quality, and Reliability.
// The breakdown is the answer to "why did this rank?"
// — a circular number alone hides the reason. The tooltip uses Chakra's
// Tooltip (with multiline text) so it works on hover, focus, and touch
// via a consistent component, with proper positioning.
export function ScoreGauge({ score, mover, small = false }: ScoreGaugeProps) {
  const pct = Math.max(0, Math.min(100, score));
  const color =
    pct >= 80 ? 'var(--oq-colors-signal-bad)' : pct >= 65 ? 'var(--oq-colors-signal-warn)' : 'var(--oq-colors-signal-warn)';

  const breakdown = mover
    ? [
        { label: 'Volume', value: mover.score_spike, weight: 0.3 },
        { label: 'Capital', value: mover.score_capital, weight: 0.35 },
        { label: 'Move', value: mover.score_move, weight: 0.35 },
        { label: 'Spread', value: mover.score_spread, weight: 0 },
        { label: 'Depth', value: mover.score_depth, weight: 0 },
        { label: 'Reliability', value: mover.reliability, weight: 0 },
      ]
    : null;

  const tooltipContent = breakdown ? (
    <Box as="pre" m="0" fontFamily="mono" fontSize="xs" lineHeight="4" whiteSpace="pre">
      {[
        `Score ${Math.round(score)}`,
        '',
        ...breakdown.map((b) => {
          const pctStr = `${(b.value * 100).toFixed(0).padStart(3)}%`;
          const weightStr = b.weight > 0 ? `  (weight ${(b.weight * 100).toFixed(0)}%)` : '  (quality)';
          return `${b.label.padEnd(11)} ${pctStr}${weightStr}`;
        }),
      ].join('\n')}
    </Box>
  ) : (
    `Score ${Math.round(score)}`
  );

  return (
    <Tooltip.Root openDelay={120} closeDelay={60} positioning={{ placement: 'top' }}>
      <Tooltip.Trigger asChild>
        <Box
          position="relative"
          display="grid"
          placeItems="center"
          borderRadius="full"
          fontWeight="semibold"
          color="text.primary"
          cursor="help"
          aria-label={`Heat score ${Math.round(score)} out of 100`}
          {...(small ? { h: '9', w: '9', fontSize: 'xs' } : { h: '12', w: '12', fontSize: 'sm' })}
          style={{
            background: `conic-gradient(${color} ${pct * 3.6}deg, var(--oq-colors-border-subtle) 0deg)`,
          }}
        >
          <Box position="absolute" inset="3px" borderRadius="full" bg="surface.panel" />
          <span style={{ position: 'relative' }}>{Math.round(score)}</span>
        </Box>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content
          bg="surface.panelElevated"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="oqControl"
          boxShadow="2xl"
          px="3"
          py="2"
          maxW="320px"
          color="text.primary"
        >
          <Tooltip.Arrow>
            <Tooltip.ArrowTip bg="surface.panelElevated" />
          </Tooltip.Arrow>
          {tooltipContent}
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  );
}